import React from 'react';
import { cn } from '@/lib/utils';

/** Note colours: rust / muted on light surfaces; peach / sheet on navy. */
export const MARGIN_NOTE_TONES = {
  rust: 'text-brand-rust',
  muted: 'text-brand-muted',
  inherit: '',
  peach: 'text-brand-peach',
  sheet: 'text-brand-sheet/80',
};

/** 23px, 25px, 31px (the reference's handnote sizes). */
export const MARGIN_NOTE_SIZES = {
  sm: 'text-[1.4375rem]',
  md: 'text-[1.5625rem]',
  lg: 'text-[1.9375rem]',
};

/**
 * A handwritten Caveat note ("small coaster. big local energy."), tilted. Decorative
 * by default: hidden from screen readers, and hidden below the 800px nav breakpoint.
 * Never put instructions, labels or anything essential in one.
 *
 * Props:
 * - `tone`: 'rust' (default) | 'muted' | 'inherit' | 'peach' | 'sheet'
 * - `size`: 'sm' | 'md' (default) | 'lg'
 * - `tilt`: rotation in degrees (default −8), set as the CSS variable --site-tilt
 * - `decorative`: true (default) = aria-hidden; false = read like normal text
 * - `hideOnMobile`: true (default) = shown from 800px up only; false = always shown
 * - `as`: element (default 'span'); positioning (absolute, offsets) via `className`
 * - `children`, other props pass through.
 */
export default function MarginNote({
  tone = 'rust',
  size = 'md',
  tilt = -8,
  decorative = true,
  hideOnMobile = true,
  as: Tag = 'span',
  className,
  children,
  ...rest
}) {
  const deg = Number.isFinite(Number(tilt)) ? Number(tilt) : -8;
  return (
    <Tag
      aria-hidden={decorative ? 'true' : undefined}
      style={{ '--site-tilt': `${deg}deg` }}
      className={cn(
        hideOnMobile ? 'hidden nav:inline-block' : 'inline-block',
        'rotate-[var(--site-tilt)] font-hand font-medium',
        // Size before leading: in cn() a text-[size] after a leading-* drops the leading
        // (tailwind-merge; see docs/guides/site-design-system.md, Known quirks).
        MARGIN_NOTE_SIZES[size] || MARGIN_NOTE_SIZES.md,
        'leading-[1.05]',
        MARGIN_NOTE_TONES[tone] ?? MARGIN_NOTE_TONES.rust,
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}
